import type { DataGridProps, GridFilterItem, GridFilterModel, GridSortModel } from '@mui/x-data-grid'
import { useSearchParams } from 'react-router-dom'
import { useCallback, useEffect, useMemo } from 'react'
import { type GridFilterItemWithId, useFilterStore } from '@/lib/stores/filter'
import { useShallow } from 'zustand/react/shallow'
import { useGridStore } from '@/lib/stores/grid'
import type { Updater } from '@tanstack/react-query'

interface PaginationParams {
  page: number
  pageSize: number
}

/**
 * Serializes filter items to a URL-safe string.
 * Returns undefined if no filter items to avoid empty param.
 *
 * Note: We serialize ALL filter items (even incomplete ones) to preserve
 * user's field/operator selection while they're still typing the value.
 * We exclude the `id` field since it's only used internally.
 */
function serializeFilters(items: GridFilterItem[]): string | undefined {
  // Serialize filters that have at least a field selected, excluding the id
  const filtersToSerialize = items.filter(item => item.field).map(({ id, ...rest }) => rest)

  if (!filtersToSerialize.length) return undefined
  return JSON.stringify(filtersToSerialize)
}

/**
 * Deserializes filter items from URL param string.
 * Returns empty array if invalid or missing.
 * Generates an id for each filter since we don't store it in the URL.
 */
function deserializeFilters(param: string | null): GridFilterItemWithId[] {
  if (!param) return []
  const parsed = JSON.parse(param)
  if (!Array.isArray(parsed)) return []
  return parsed.map((item, index) => ({ ...item, id: index.toString() }))
}

/**
 * Serializes sort model to URL params format.
 * Returns object with sortBy and sortDirection, or empty if no sort.
 */
function serializeSort(model: GridSortModel): {
  sortBy?: string
  sortDirection?: string
} {
  if (!model.length || !model[0].sort) return {}
  return {
    sortBy: model[0].field,
    sortDirection: model[0].sort
  }
}

/**
 * Deserializes sort model from URL params.
 */
function deserializeSort(sortBy: string | null, sortDirection: string | null): GridSortModel {
  if (!sortBy) return []
  const direction = sortDirection === 'desc' ? 'desc' : 'asc'
  return [{ field: sortBy, sort: direction }]
}

interface UseDataGridConfigOptions {
  /** Unique identifier for this table instance (for component isolation) */
  tableId: string
  /** Whether to sync filters to URL params (default: true for primary tables) */
  syncToUrl?: boolean
}

function applyFilter<T>(row: T, filter: GridFilterItem): boolean {
  const value = (row as Record<string, unknown>)[filter.field]
  const filterValue = filter.value

  if (value === undefined || value === null) {
    return filter.operator === 'isEmpty'
  }

  const strValue = String(value).toLowerCase()
  const strFilterValue = filterValue !== undefined ? String(filterValue).toLowerCase() : ''

  switch (filter.operator) {
    case 'contains':
      return strValue.includes(strFilterValue)
    case 'doesNotContain':
      return !strValue.includes(strFilterValue)
    case 'equals':
      return strValue === strFilterValue
    case 'doesNotEqual':
      return strValue !== strFilterValue
    case 'startsWith':
      return strValue.startsWith(strFilterValue)
    case 'endsWith':
      return strValue.endsWith(strFilterValue)
    case 'isEmpty':
      return value === undefined || value === null || value === ''
    case 'isNotEmpty':
      return value !== undefined && value !== null && value !== ''
    case 'is':
      if (typeof value === 'boolean') return value === filterValue
      if (value instanceof Date || typeof value === 'number') return String(value) === String(filterValue)
      return strValue === strFilterValue
    case 'isNot':
      if (typeof value === 'boolean') return value !== filterValue
      return strValue !== strFilterValue
    case 'isAfter':
      return new Date(value as string) > new Date(filterValue as string)
    case 'isOnOrAfter':
      return new Date(value as string) >= new Date(filterValue as string)
    case 'isBefore':
      return new Date(value as string) < new Date(filterValue as string)
    case 'isOnOrBefore':
      return new Date(value as string) <= new Date(filterValue as string)
    case 'greaterThan':
      return Number(value) > Number(filterValue)
    case 'greaterThanOrEqual':
      return Number(value) >= Number(filterValue)
    case 'lessThan':
      return Number(value) < Number(filterValue)
    case 'lessThanOrEqual':
      return Number(value) <= Number(filterValue)
    default:
      return true
  }
}

export function filterRows<T>(rows: T[], filters: GridFilterItem[]): T[] {
  const validFilters = filters.filter(f => f.field && f.operator)
  if (validFilters.length === 0) return rows

  return rows.filter(row => validFilters.every(filter => applyFilter(row, filter)))
}

export const useDataGridConfig = ({ tableId, syncToUrl = true }: UseDataGridConfigOptions) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const filters = useFilterStore(useShallow(state => state.filters[tableId] ?? []))
  const setFilters = useCallback(
    (filters: Updater<GridFilterItemWithId[], GridFilterItemWithId[]>): GridFilterItemWithId[] => {
      const { filters: storeFilters, setFilters: setStoreFilters } = useFilterStore.getState()
      if (typeof filters === 'function') {
        const result = filters(storeFilters[tableId] ?? [])
        setStoreFilters(tableId, result)

        return result
      } else {
        setStoreFilters(tableId, filters)
        return filters
      }
    },
    [tableId]
  )

  const columnVisibilityModel = useGridStore(st => st.columnVisibilityModel)

  // Extract specific params to use as stable dependencies
  const pageParam = searchParams.get('page')
  const pageSizeParam = searchParams.get('pageSize')
  const filtersParam = searchParams.get('filters')
  const searchQueryParams = searchParams.getAll('searchQuery').join(',')
  const sortByParam = searchParams.get('sortBy')
  const sortDirectionParam = searchParams.get('sortDirection')

  // Parse filters from URL (only when syncToUrl is true)
  const urlFilters = useMemo(() => deserializeFilters(filtersParam), [filtersParam])

  // Sync URL → store on mount and when URL changes (only for syncToUrl tables)
  // biome-ignore lint/correctness/useExhaustiveDependencies: tableId should be here.
  useEffect(() => {
    if (syncToUrl) {
      setFilters(urlFilters)
    }
  }, [syncToUrl, tableId, urlFilters, setFilters])

  // Parse pagination from URL (with defaults)
  const paginationModel = useMemo(
    () => ({
      page: parseInt(pageParam ?? '0'),
      pageSize: parseInt(pageSizeParam ?? '10')
    }),
    [pageParam, pageSizeParam]
  )

  // MUI filterModel - always empty items, we handle filtering via our store
  // For client-side filtering, the rows are filtered before being passed to DataGrid
  const filterModel: GridFilterModel = useMemo(
    () => ({
      items: [],
      quickFilterValues: searchQueryParams ? searchQueryParams.split(',') : []
    }),
    [searchQueryParams]
  )

  // Parse sort model from URL
  const sortModel: GridSortModel = useMemo(
    () => deserializeSort(sortByParam, sortDirectionParam),
    [sortByParam, sortDirectionParam]
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: setSearchParams is stable
  const onPaginationModelChange = useCallback((data: PaginationParams) => {
    setSearchParams(_ => {
      // Use window.location.search which ensures the most up-to-date search params
      // versus the prev argument here.
      const next = new URLSearchParams(window.location.search)

      next.delete('page')
      next.delete('pageSize')
      if (data.page !== 0) {
        next.set('page', data.page.toString())
      }
      if (data.pageSize !== 10) {
        next.set('pageSize', data.pageSize.toString())
      }
      return next
    })
  }, [])

  // Handler for filter changes from our custom FilteringButton
  // biome-ignore lint/correctness/useExhaustiveDependencies: setSearchParams is stable
  const handleFiltersChange = useCallback(
    (newFilters: Updater<GridFilterItemWithId[], GridFilterItemWithId[]>) => {
      // Always update the store
      const filters = setFilters(newFilters)

      // Sync to URL only if syncToUrl is enabled
      if (syncToUrl) {
        setSearchParams(_ => {
          // Use window.location.search which ensures the most up-to-date search params
          // versus the prev argument here.
          const next = new URLSearchParams(window.location.search)

          next.delete('page') // Reset to page 0 when filters change
          next.delete('filters')

          const serialized = serializeFilters(filters)
          if (serialized) {
            next.set('filters', serialized)
          }
          return next
        })
      }
    },
    [tableId, syncToUrl, setFilters]
  )

  // Legacy MUI onFilterModelChange - only handles quickFilterValues now
  // biome-ignore lint/correctness/useExhaustiveDependencies: setSearchParams is stable
  const onFilterModelChange: DataGridProps['onFilterModelChange'] = useCallback((model: GridFilterModel) => {
    setSearchParams(_ => {
      // Use window.location.search which ensures the most up-to-date search params
      // versus the prev argument here.
      const next = new URLSearchParams(window.location.search)

      // Handle search query (MUI calls this quickFilterValues)
      next.delete('searchQuery')
      if (Array.isArray(model.quickFilterValues) && model.quickFilterValues.length) {
        model.quickFilterValues.forEach(value => {
          next.append('searchQuery', value)
        })
      }

      return next
    })
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: setSearchParams is stable
  const onSortModelChange: DataGridProps['onSortModelChange'] = useCallback((model: GridSortModel) => {
    setSearchParams(_ => {
      // Use window.location.search which ensures the most up-to-date search params
      // versus the prev argument here.
      const next = new URLSearchParams(window.location.search)
      next.delete('sortBy')
      next.delete('sortDirection')

      const { sortBy, sortDirection } = serializeSort(model)
      if (sortBy) {
        next.set('sortBy', sortBy)
        if (sortDirection) {
          next.set('sortDirection', sortDirection)
        }
      }

      return next
    })
  }, [])

  return {
    // Identifier for the table instance
    tableId,

    // Pagination
    paginationModel,
    onPaginationModelChange,

    // Filters - our custom system
    filters,
    handleFiltersChange,

    // MUI filterModel - empty, only for quickFilterValues
    filterModel,
    onFilterModelChange,

    // Sorting
    sortModel,
    onSortModelChange,

    // Column visibility
    onColumnVisibilityModelChange: (model: DataGridProps['columnVisibilityModel']) => setColumnVisibilityModel(model),

    // Initial state for MUI DataGrid
    initialState: {
      columns: {
        columnVisibilityModel
      },
      pagination: {
        paginationModel: paginationModel
      },
      filter: {
        filterModel: { items: [] } // Always empty for MUI
      },
      sorting: {
        sortModel
      }
    }
  }
}

const { setColumnVisibilityModel } = useGridStore.getState()
